import React, { useEffect, useRef, forwardRef, useState } from "react";
import styles from "./styles/InputDate.module.css";
import { ReactComponent as CalendarIcon } from "../../../assets/calendar.svg";
import { capitalizeFirstLetter } from "../../../utils/utils";

/**
 * Input date picker component
 */
const InputDate = forwardRef(
    (
        {
            label, // {String} - The label text for the input
            inputId, // {String} - The ID of the input element
            inputValue, // {Date} - The value of the input (date)
            handleChangeFunction, // {Function} - The function to handle input changes
            required, // {Boolean} - Flag to indicate if the input is required
            fromToday, // {Boolean} - Flag to indicate if the input should start from today's date
            toToday, // {Boolean} - Flag to indicate if the input should end at today's date
            clearTrigger,
            disable,
            data,
            placeholder,
            fullWidth,
        },
        ref
    ) => {
        const dateInputRef = useRef(null);
        const [trigerRequired, setTrigerRequired] = useState(false);

        const today = new Date();
        const todayDate = today.toISOString().split("T")[0];
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const tomorrowDate = tomorrow.toISOString().split("T")[0];
        const pastDate = new Date(today.setFullYear(today.getFullYear() - 25))

            .toISOString()
            .split("T")[0];

        const handleIconClick = () => {
            if (dateInputRef.current) {
                dateInputRef.current.focus();
                dateInputRef.current.showPicker();
            }
        };

        const timerRef = useRef();

        useEffect(() => {
            if (dateInputRef.current) {
                dateInputRef.current.value = "";
            }
        }, [clearTrigger]);

        useEffect(() => {
            if (trigerRequired) {
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setTrigerRequired(false);
                }, 2000);
            }
        }, [trigerRequired]);

        function mergeRefs(...refs) {
            return (element) => {
                refs.forEach((ref) => {
                    if (typeof ref === "function") {
                        ref(element);
                    } else if (ref != null) {
                        ref.current = element;
                    }
                });
            };
        }

        const handleValidationOnBlur = (event) => {
            const { value } = event.target;

            if (!value) return;

            const selected = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (
                fromToday &&
                selected.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0)
            ) {
                event.target.value = "";
                handleChangeFunction({ target: { name: inputId, value: "" } });
                setTrigerRequired(true);
                return;
            }

            if (
                toToday &&
                selected.setHours(0, 0, 0, 0) > today.setHours(0, 0, 0, 0)
            ) {
                event.target.value = "";
                handleChangeFunction({ target: { name: inputId, value: "" } });
                setTrigerRequired(true);

                return;
            }

            event.target.setCustomValidity("");
        };

        return (
            <div
                className={`${styles.input_container} ${
                    fullWidth ? styles.full_width : ""
                } v-flex-container`}
            >
                <label className={`${styles.input_label}`} htmlFor={inputId}>
                    {label}:
                </label>
                <div
                    className={`${styles.input_element} ${
                        disable ? styles.disabled : ""
                    } ${required || trigerRequired ? styles.alert : ""} `}
                >
                    <CalendarIcon
                        onClick={disable ? null : handleIconClick}
                        className={`${styles.calendar_icon} ${
                            disable ? styles.calendar_icon_disabled : ""
                        } icon-component`}
                    />

                    <input
                        data-ex={data && data}
                        ref={mergeRefs(ref, dateInputRef)}
                        className={`${styles.input_element_date}`}
                        type={required ? "text" : "date"}
                        name={inputId}
                        id={inputId}
                        placeholder={
                            required ? capitalizeFirstLetter(placeholder) : ""
                        }
                        value={inputValue}
                        onChange={handleChangeFunction}
                        onBlur={handleValidationOnBlur}
                        min={fromToday ? todayDate : pastDate}
                        max={toToday ? todayDate : undefined}
                        disabled={disable}
                    />
                </div>
            </div>
        );
    }
);

export default InputDate;
